import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert
} from '@mui/material';

// Icons for different file types
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DescriptionIcon from '@mui/icons-material/Description';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import DataObjectIcon from '@mui/icons-material/DataObject';
import TableChartIcon from '@mui/icons-material/TableChart';
import ArchiveIcon from '@mui/icons-material/Archive';

const UniversalFileViewer = ({ fileUrls = [] }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

 
  useEffect(() => {
    if (fileUrls && fileUrls.length > 0) {
      try {
        const processedFiles = fileUrls.map(url => {
          const fileName = url.split('/').pop();
          return {
            name: fileName,
            type: getFileType(fileName),
            size: 0, // Cannot determine size from URL alone
            url: url,
          };
        });
        
        setFiles(currentFiles => {
          // Filter out any existing files that match the new URLs to avoid duplicates
          const existingFileUrls = new Set(currentFiles.map(f => f.url));
          const newFiles = processedFiles.filter(f => !existingFileUrls.has(f.url));
          return [...currentFiles, ...newFiles];
        });
      } catch (err) {
        setError("Error processing file URLs. Please check the format.");
        console.error(err);
      }
    }
  }, [fileUrls]);

  // Function to determine file type
  const getFileType = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    
    // Image types
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension)) {
      return 'image';
    }
    // Document types
    else if (['pdf'].includes(extension)) {
      return 'pdf';
    }
    else if (['doc', 'docx'].includes(extension)) {
      return 'word';
    }
    else if (['xls', 'xlsx', 'csv'].includes(extension)) {
      return 'spreadsheet';
    }
    // Media types
    else if (['mp3', 'wav', 'ogg', 'flac', 'm4a'].includes(extension)) {
      return 'audio';
    }
    else if (['mp4', 'webm', 'mov', 'avi', 'mkv'].includes(extension)) {
      return 'video';
    }
    // Code/text types
    else if (['txt', 'rtf', 'md'].includes(extension)) {
      return 'text';
    }
    else if (['json', 'xml', 'html', 'css', 'js', 'jsx', 'py', 'java', 'cpp'].includes(extension)) {
      return 'code';
    }
    // Archive types
    else if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension)) {
      return 'archive';
    }
    // Default
    else {
      return 'unknown';
    }
  };

  // Function to get the appropriate icon
  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'image':
        return <ImageIcon sx={{ fontSize: 50, color: 'primary.main' }} />;
      case 'pdf':
        return <PictureAsPdfIcon sx={{ fontSize: 50, color: 'error.main' }} />;
      case 'word':
        return <DescriptionIcon sx={{ fontSize: 50, color: 'info.main' }} />;
      case 'spreadsheet':
        return <TableChartIcon sx={{ fontSize: 50, color: 'success.main' }} />;
      case 'audio':
        return <AudioFileIcon sx={{ fontSize: 50, color: 'warning.main' }} />;
      case 'video':
        return <VideoFileIcon sx={{ fontSize: 50, color: 'secondary.main' }} />;
      case 'text':
        return <InsertDriveFileIcon sx={{ fontSize: 50, color: 'text.primary' }} />;
      case 'code':
        return <DataObjectIcon sx={{ fontSize: 50, color: 'info.dark' }} />;
      case 'archive':
        return <ArchiveIcon sx={{ fontSize: 50, color: 'warning.dark' }} />;
      default:
        return <InsertDriveFileIcon sx={{ fontSize: 50, color: 'text.secondary' }} />;
    }
  };

  // Handle file upload
//   const handleFileUpload = (event) => {
//     setLoading(true);
//     setError(null);
    
//     try {
//       const newFiles = Array.from(event.target.files).map(file => ({
//         name: file.name,
//         type: getFileType(file.name),
//         size: file.size,
//         url: URL.createObjectURL(file),
//         file: file
//       }));
      
//       setFiles(prevFiles => [...prevFiles, ...newFiles]);
//     } catch (err) {
//       setError("Error processing files. Please try again.");
//       console.error(err);
//     } finally {
//       setLoading(false);
//     }
//   };

  // Handle file removal
  const handleFileRemove = (fileToRemove) => {
    setFiles(prevFiles => prevFiles.filter(file => file.url !== fileToRemove.url));
 
    if (fileToRemove.file) {
      URL.revokeObjectURL(fileToRemove.url);
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '-';  
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Render file preview
  const renderFilePreview = (file) => {
    const { type, url, name } = file;
    
    switch (type) {
      case 'image':
        return (
          <Box sx={{ width: '100%', height: '70px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <img
              src={url}
              alt={name}
              style={{ maxWidth: '100%', maxHeight: '100px', objectFit: 'contain' }}
            />
          </Box>
        );
      case 'pdf':
        return (
          <Box sx={{ textAlign: 'center',height: '70px' }}>
            {getFileIcon(type)}
            {/* <Button 
              variant="contained" 
              href={url} 
              target="_blank" 
              rel="noopener noreferrer"
              sx={{ mt: 2 }}
            >
              View PDF
            </Button> */}
          </Box>
        );
      case 'video':
        return (
          <Box sx={{ width: '100%',height: '70px' }}>
            <video 
              controls 
              style={{ width: '100%', maxHeight: '80px' }}
            >
              <source src={url} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </Box>
        );
      case 'audio':
        return (
          <Box sx={{ width: '100%',height: '70px', textAlign: 'center' }}>
            {getFileIcon(type)}
            <audio 
              controls 
              style={{ width: '100%',  maxHeight: '80px', marginTop: '12px' }}
            >
              <source src={url} />
              Your browser does not support the audio element.
            </audio>
          </Box>
        );
      default:
        return (
          <Box sx={{ textAlign: 'center',height: '70px' }}>
            {getFileIcon(type)}
            {/* <Button 
              variant="contained" 
              href={url} 
              target="_blank" 
              rel="noopener noreferrer"
              sx={{ mt: 2 }}
            >
              Open File
            </Button> */}
          </Box>
        );
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* <Typography variant="h4" gutterBottom>Universal File Viewer</Typography> */}
      
      {/* <Box sx={{ mb: 4 }}>
        <Button
          variant="contained"
          component="label"
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Upload Additional Files'}
          <input
            type="file"
            hidden
            multiple
            onChange={handleFileUpload}
          />
        </Button>
      </Box> */}
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      )}
      
      {files.length > 0 ? (
        <Grid container spacing={3}>
          {files.map((file, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card  >
                <CardContent>
                  <Box sx={{ mb: 2 }}>
                    {renderFilePreview(file)}
                  </Box>
                  
                  <Typography variant="subtitle1" noWrap title={file.name}  >
                    {file.name}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary">
                    {formatFileSize(file.size)}
                  </Typography>
                  
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between',gap:1 }}>
                    <Button 
                      size="small" 
                      variant="outlined" 
                      href={file.url} 
                      target="_blank" 
                      download={file.name}  
                      sx={{ px: 5 }}
                    >
                      View
                    </Button>
                    {/* <Button 
                      size="small" 
                      variant="outlined" 
                      color="error" 
                      onClick={() => handleFileRemove(file)}
                    >
                      Remove
                    </Button> */}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Typography variant="body1" color="text.secondary" align="center">
          No files uploaded yet. Click the upload button to get started.
        </Typography>
      )}
    </Box>
  );
};

export default UniversalFileViewer;